import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckChip = ({ checked, onToggle, label, testid }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-sm border transition ${
      checked
        ? 'bg-amber-400 text-black border-amber-400'
        : 'bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-amber-400/50'
    }`}
  >
    {label}
  </button>
);

const SubscribePage = () => {
  const [email, setEmail] = useState('');
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [minDiscount, setMinDiscount] = useState(20);
  const [priceMax, setPriceMax] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [meta, setMeta] = useState({ categories: [], stores: [] });
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    axios.get(`${API}/deals/meta`).then(r => setMeta(r.data)).catch(() => {});
  }, []);

  const toggle = (list, setList, v) => {
    setList(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const payload = {
        email,
        categories,
        stores,
        min_discount: Number(minDiscount) || 0,
        price_max: priceMax ? Number(priceMax) : null,
        frequency,
      };
      const r = await axios.post(`${API}/deals/subscribe`, payload);
      setToken(r.data.unsubscribe_token || '');
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Subscription failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="text-5xl mb-4">🎯</div>
        <h1 data-testid="subscribe-success-title" className="text-3xl font-extrabold">You're in.</h1>
        <p className="text-zinc-300 mt-2">
          We'll email curated hot deals to <span className="text-amber-400">{email}</span> based on your preferences.
        </p>
        {token && (
          <p className="text-xs text-zinc-500 mt-4">
            Save this link to manage preferences later:<br/>
            <code className="text-zinc-300 break-all">/preferences/{token}</code>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 data-testid="subscribe-title" className="text-3xl sm:text-4xl font-extrabold">Get hand-picked deals in your inbox</h1>
      <p className="text-zinc-400 mt-2">
        Tell us what you shop for and how often — we'll send you a clean, curated digest.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div>
          <label className="block text-sm font-semibold text-zinc-200 mb-1">Email address</label>
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            data-testid="subscribe-email-input"
            placeholder="you@example.com"
            className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-4 py-3 focus:outline-none focus:border-amber-400"
          />
        </div>

        <div>
          <div className="text-sm font-semibold text-zinc-200 mb-2">Favorite categories</div>
          <div className="flex flex-wrap gap-2">
            {meta.categories.map(c => (
              <CheckChip
                key={c.slug}
                label={c.name}
                checked={categories.includes(c.slug)}
                onToggle={() => toggle(categories, setCategories, c.slug)}
                testid={`sub-cat-${c.slug}`}
              />
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm font-semibold text-zinc-200 mb-2">Favorite stores</div>
          <div className="flex flex-wrap gap-2">
            {meta.stores.filter(s => s.slug !== 'other').map(s => (
              <CheckChip
                key={s.slug}
                label={s.name}
                checked={stores.includes(s.slug)}
                onToggle={() => toggle(stores, setStores, s.slug)}
                testid={`sub-store-${s.slug}`}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-zinc-200 mb-1">Min discount</label>
            <select
              value={minDiscount}
              onChange={e => setMinDiscount(e.target.value)}
              data-testid="sub-min-discount"
              className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2.5"
            >
              <option value="0">Any</option>
              <option value="10">10%+</option>
              <option value="20">20%+</option>
              <option value="30">30%+</option>
              <option value="50">50%+</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-zinc-200 mb-1">Max price ($)</label>
            <input
              type="number"
              value={priceMax}
              onChange={e => setPriceMax(e.target.value)}
              data-testid="sub-price-max"
              placeholder="No limit"
              className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2.5"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-zinc-200 mb-1">Email frequency</label>
            <select
              value={frequency}
              onChange={e => setFrequency(e.target.value)}
              data-testid="sub-frequency"
              className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2.5"
            >
              <option value="daily">Daily digest</option>
              <option value="weekly">Weekly</option>
              <option value="instant">Instant hot-deal alerts</option>
              <option value="off">Off</option>
            </select>
          </div>
        </div>

        {err && <div data-testid="subscribe-error" className="text-red-400 text-sm">{err}</div>}

        <button
          type="submit"
          disabled={loading}
          data-testid="subscribe-submit"
          className="w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-semibold px-6 py-3"
        >
          {loading ? 'Subscribing…' : 'Start receiving deals'}
        </button>
      </form>
    </div>
  );
};

export default SubscribePage;
